/* eslint-disable no-nested-ternary */
import React, {
  useContext, useEffect, useState,
} from 'react';
import moment from 'moment-timezone';
import PropTypes from 'prop-types';
import { Link } from 'react-router-dom';
import UnfoldMore from '@mui/icons-material/UnfoldMore';
import { Button } from '@mui/material';
import { Navbar, Sidebar } from '../../components';
import { sendRequest } from '../../api/shootAPI';
import { NotificationContext } from '../../contexts/Notification';
import { UserContext } from '../../contexts/User';
import API from '../../api/endpoints';
import DateRangePicker from '../../components/dateRangePicker';
import IconHelp from '../../images/icon-help.svg';
// import IconInfo from '../../images/icon-info-green.svg';
import AlertIcon from '../../images/alert.png';

import './analytics.css';
import TeamSelect from '../userDashboard/TeamSelect';
import { formatSeconds } from '../../utils/time';
import CustomTable from '../../components/table/CustomTable';
import Pagination from '../../components/pagination';
import useDebounce from '../../hooks/useDebounce';
import TimezoneSelectWrapper from '../../components/selectors/TimezoneSelectWrapper';

const TABLE_HEADERS = [
  {
    label: 'Member',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'name',
    colSpan: 1.5,
  },
  {
    label: 'Total Time',
    sortable: true,
    sortIcon: UnfoldMore,
    sortKey: 'designation',
    colSpan: 1.5,
  },
  {
    label: 'Previous 60 days',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'teamsAssigned',
    colSpan: 1.5,
  },
  {
    label: 'Confidence',
    sortable: false,
    sortIcon: UnfoldMore,
    sortKey: 'teamsAssigned',
    colSpan: 1.5,
  },
  {
    label: 'Action',
    sortable: false,
  },
];
const CustomTableStyles = {
  container: {
    marginTop: 0,
  },
  header: {
    backgroundColor: 'var(--dark-10)',
    fontSize: '14px',
    '.MuiTableSortLabel-iconDirectionAsc': {
      transform: 'rotate(0deg) !important',
    },
    borderBottom: '1px solid var(--Dark-Dark-06, #E0E1E6) !important',
    borderRadius: '0 !important',
  },
  row: {
    '&:nth-of-type(odd)': {
      backgroundColor: '#fff !important',
    },
    '&:hover': {
      backgroundColor: 'var(--dark-10) !important',
    },
  },
  cell: {
    button: {
      textWrap: 'nowrap',
      color: 'var(--dark-2)',
      borderColor: 'var(--dark-4) !important',
    },
  },
};

function Card({ title, description, subTitle = '' }) {
  return (
    <div className="card-wrapper">
      <div>
        <div className="help-text text-600">
          {title}
        </div>
        {
                subTitle ? (
                  <div className="mt-1 card-sub-title">{subTitle}</div>
                ) : ''
            }
      </div>
      <div className="card-text">
        {description}
      </div>
    </div>
  );
}

Card.propTypes = {
  title: PropTypes.string,
  description: PropTypes.string,
  subTitle: PropTypes.string,
};

function UnusualActivity() {
  const { activeWorkspace } = useContext(UserContext);
  const [teams, setTeams] = useState([]);
  const [selectedTeamId, setSelectedTeamId] = useState(0);
  const [membersTableData, setMembersTableData] = useState([]);
  // const [instances, setInstances] = useState(instanceData);
  const [summary, setSummary] = useState({
    totalMembers: 0, totalInstances: 0, totalTime: 0,
  });
  const [selectedTimezone, setSelectedTimezone] = useState(activeWorkspace.timezone);
  const [date, setDate] = useState(() => {
    const initialToday = moment.tz(selectedTimezone).toDate();
    return [moment.tz(selectedTimezone).subtract(6, 'days').toDate(), initialToday];
  });
  const [totalCounts, setTotalCounts] = useState(0);
  const { displayNotification } = useContext(NotificationContext);
  const [isLoading, setIsLoading] = useState(false);
  const [pageSize, setPageSize] = useState(10);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const { debounce } = useDebounce();
  // mockup start
  // const totalTime = 92303;
  // end

  const fetchTeams = async () => {
    const { success, data } = await sendRequest({
      method: 'get',
      url: API.WORKSPACE.getAllTeamInfo(activeWorkspace.id),
      timeout: 30000,
    });
    if (success) {
      setTeams(data.teams);
    } else {
      displayNotification({
        message: 'There was an issue fetching team data. Please try again',
        severity: 'error',
      });
    }
  };

  const fetchMembers = async () => {
    setIsLoading(true);

    // Convert dates to UTC using selected timezone
    const startOfDay = moment.tz(date[0], selectedTimezone)
      .startOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const endOfDay = moment.tz(date[1], selectedTimezone)
      .endOf('day')
      .clone()
      .utc()
      .format('YYYY-MM-DD HH:mm:ss');

    const { success, data: res } = await sendRequest({
      method: 'get',
      url: API.UNUSUAL_ACTIVITY.getWorkspaceSummary(activeWorkspace.id),
      params: {
        startDate: startOfDay,
        endDate: endOfDay,
        teamId: selectedTeamId,
        page: currentPage,
        pageSize,
        timezone: selectedTimezone,
      },
      timeout: 30000,
    });

    const { data } = res ?? { success: false, data: { members: [] } };
    if (success) {
      const mappedMembers = data.members.map((member) => {
        const {
          memberId, totalTime, previousDays, confidence,
        } = member;
        return ({
          name: member.member.user.fullName,
          totalTime: formatSeconds(totalTime ?? 0, { showSeconds: true, roundToNearestMinute: false }),
          previousDays: formatSeconds(previousDays ?? 0, { showSeconds: true, roundToNearestMinute: false }),
          confidence: (
            <div className={`table-confidence-${confidence.toLowerCase()}`}>
              {confidence}
            </div>
          ),
          actions: (
            <Link to={`/analytics/unusual-activity/${memberId}`}>
              <Button
                variant="outlined"
                style={{ textWrap: 'no-wrap' }}
              >
                View
              </Button>
            </Link>
          ),
        });
      });
      setMembersTableData(mappedMembers);
      setTotalCounts(data.pagination.totalCounts);
      setTotalPages(data.pagination.totalPages);
      setSummary(data.summary);
    } else {
      displayNotification({
        message: 'There was an issue fetching unusual activity. Please try again',
        severity: 'error',
      });
    }
    setIsLoading(false);
  };

  // const fetchInstances = async () => {

  // };

  const handleSort = debounce(() => {
    // const handleSort = debounce((pSortKey, pSortOrder) => {
    // setSortKey(pSortKey);
    // setSortOrder(pSortOrder);
  }, 250);

  const handlePageChange = (page) => {
    setCurrentPage(page);
  };

  const handlePageSizeChange = (pageSize) => {
    setCurrentPage(1);
    setTotalPages(Math.ceil(totalCounts / pageSize));
    setPageSize(pageSize);
  };

  useEffect(() => {
    fetchMembers();
  }, [selectedTeamId, date, currentPage, pageSize, selectedTimezone]);

  useEffect(() => {
    fetchTeams();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  return (
    <>
      <Navbar />
      <Sidebar />
      <div className="screen-wrapper">
        <div className="screen-header">
          <div className="screen-header-container d-flex justify-content-between position-relative">
            <div className="w-100 d-flex justify-content-between align-center">
              <div className="header-manage-member w-auto text-600">
                Unusual Activity
              </div>
              <div className="d-flex gap-4">
                <div className="d-flex align-items-center">
                  <img src={IconHelp} width={20} height={20} alt="help" />
                  <div className="help-text">
                    About Unusual Activity
                  </div>
                </div>
                {/* <Button className="smart-notification-btn d-flex gap-1 align-items-center"> */}
                {/*  <img src={IconNotification} width={20} height={20} alt="notification" /> */}
                {/*  <div className="help-text"> */}
                {/*    Smart Notifications */}
                {/*  </div> */}
                {/* </Button> */}
                {/* <Button className="smart-notification-btn"> */}
                {/*  <img src={IconFormatList} alt="list" /> */}
                {/* </Button> */}
              </div>
            </div>
          </div>
          <div className="d-flex gap-3 mb-3">
            <div className="team-select-wrapper">
              <TeamSelect
                menuItems={teams}
                value={selectedTeamId}
                handleForm={(e) => setSelectedTeamId(e.target.value)}
              />
            </div>
            <div className="date-picker-wrapper">
              <DateRangePicker
                value={date}
                onChange={(_date) => setDate(_date)}
              />
            </div>
            <div className="timezone-select-wrapper">
              <TimezoneSelectWrapper
                value={selectedTimezone}
                onChange={(e) => setSelectedTimezone(e.target.value)}
              />
            </div>
          </div>
          <div className="d-flex gap-3 justify-space-between">
            <Card title="Members" description={summary.totalMembers} />
            <Card title="Instances" description={summary.totalInstances} />
            <Card
              title="Total Time (hh:mm:ss)"
              description={moment.utc(summary.totalTime * 1000).format('HH:mm:ss')}
            />
          </div>
          <div className="table-card my-3">
            <div className="table-card-header gap-2">
              <img src={AlertIcon} width={20} height={20} alt="alert" />
              Members with unusual activity
            </div>
            <CustomTable
              headers={TABLE_HEADERS}
              items={membersTableData}
              loading={isLoading}
              handleSort={handleSort}
              styles={CustomTableStyles}
            />
            <Pagination
              currentPage={currentPage}
              pageSize={pageSize}
              totalPages={totalPages}
              totalCounts={totalCounts}
              onPageChange={handlePageChange}
              setPageSize={handlePageSizeChange}
              styles={{ margin: 0 }}
            />
          </div>
        </div>
      </div>
    </>
  );
}

export default UnusualActivity;
